/** Human label for a variant: its variation titles joined, or a short id.
 *  A variation type with a single variation adds no distinction between variants,
 *  so it's omitted — unless every type is single (a one-variant item), in which
 *  case they're kept so the variant still has a name. */
export const variationLabel = (item, variant) => {
    const ids = variant.variationIds || [];
    const allTypes = item.variationTypes || [];
    const distinguishing = allTypes.filter((vt) => (vt.variations || []).length > 1);
    const types = distinguishing.length ? distinguishing : allTypes;
    const parts = [];
    types.forEach((vt) => {
        const match = (vt.variations || []).find((v) => ids.includes(v.id));
        if (match) parts.push(match.title);
    });
    return parts.length ? parts.join(' / ') : `Variant ${variant.id.slice(0, 8)}…`;
};

/** The item's variants sorted by their display label (numeric-aware, so sizes
 *  order 7 → 10 → 16). The DB returns variants in arbitrary order that shifts
 *  after updates — a stable sort keeps the step UIs from reshuffling. */
export const sortedVariants = (item) =>
    [...(item.variants || [])].sort((a, b) =>
        variationLabel(item, a).localeCompare(variationLabel(item, b), undefined, { numeric: true }),
    );

/** Variation types that change only the surface, not the shape (mirrors the
 *  backend's geometry-group.ts — keep in sync). */
const GEOMETRY_NEUTRAL_VARIATION_TITLES = ['a color', 'a texture'];

/** Key identifying the variant's geometry: its variation ids minus those of
 *  color/texture types. Variants with the same key share one 3D model — only one
 *  is generated, the rest are retextured from it. Mirrors the backend's
 *  geometryGroupKey in geometry-group.ts. */
export const geometryGroupKey = (item, variant) => {
    const neutralIds = new Set(
        (item.variationTypes || [])
            .filter((vt) => GEOMETRY_NEUTRAL_VARIATION_TITLES.includes((vt.title || '').trim().toLowerCase()))
            .flatMap((vt) => (vt.variations || []).map((v) => v.id)),
    );
    const shapeIds = (variant.variationIds || []).filter((id) => !neutralIds.has(id));
    return shapeIds.length ? [...shapeIds].sort().join('|') : '__single__';
};

/** Download filename for an asset: its stored basename, else `<variantId>.<format>`. */
export const fileNameOf = (asset, variantId) =>
    (asset.s3Key && asset.s3Key.split('/').pop()) || `${variantId}.${asset.assetFormat}`;

/** Friendlier names for generated-asset labels (the label stays what the pipeline keys on). */
const ASSET_LABEL_NAMES = { 'opus-build': 'opus-model', 'opus-retexture': 'opus-retexture', 'meshy-raw': 'meshy-model' };

/**
 * Display names for a variant's assets, keyed by asset id: `<label>.<format>`,
 * numbered `-v1`, `-v2`, … (oldest first) when the same label occurs more than
 * once, so the latest of several builds is clear at a glance.
 */
export const assetDisplayNames = (assets) => {
    const byLabel = {};
    [...(assets || [])]
        .sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0))
        .forEach((a) => {
            const label = a.label || a.assetFormat;
            (byLabel[label] = byLabel[label] || []).push(a);
        });
    const names = {};
    Object.entries(byLabel).forEach(([label, list]) => {
        const base = ASSET_LABEL_NAMES[label] || label;
        list.forEach((a, i) => {
            names[a.id] = list.length > 1 ? `${base}-v${i + 1}.${a.assetFormat}` : `${base}.${a.assetFormat}`;
        });
    });
    return names;
};

/** The variation titles that define a geometry group, e.g. "Size — Big" (empty for a single-model item). */
export const geometryGroupLabel = (item, variant) => {
    const neutral = new Set(GEOMETRY_NEUTRAL_VARIATION_TITLES);
    const ids = new Set(variant.variationIds || []);
    const parts = (item.variationTypes || [])
        .filter((vt) => !neutral.has((vt.title || '').trim().toLowerCase()) && (vt.variations || []).length > 1)
        .map((vt) => {
            const match = (vt.variations || []).find((v) => ids.has(v.id));
            return match ? `${variationTypeLabel(vt.title)} — ${match.title}` : null;
        })
        .filter(Boolean);
    return parts.join(' · ');
};

/** "a color" → "Color" (the Step-1 dropdown's label for the type). */
const variationTypeLabel = (title) => {
    const t = (title || '').trim();
    const stripped = t.replace(/^(a|an)\s+/i, '');
    return stripped ? stripped.charAt(0).toUpperCase() + stripped.slice(1) : t;
};

/** Sentence-cases a name: "IRON LAMP VERY COOL" -> "Iron lamp very cool". */
export const toSentenceCase = (s) => {
    const t = (s ?? '').trim();
    return t ? t.charAt(0).toUpperCase() + t.slice(1).toLowerCase() : t;
};

export const formatBytes = (n) => {
    if (!n) return '—';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(2)} MB`;
};
